import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, ArrowRight } from 'lucide-react';
import { convertAPI, walletAPI } from '../services/api';
import { CRYPTO_NAMES, CRYPTO_SYMBOLS, formatCurrency } from '../utils/constants';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CRYPTO_RATES = {
  BTC: 220000.0,
  ETH: 12000.0,
  USDT: 3.67,
  USDC: 3.67,
  BNB: 2200.0,
  SOL: 550.0
};

const Convert = () => {
  const [balances, setBalances] = useState([]);
  const [convertData, setConvertData] = useState({
    from_crypto: 'BTC',
    amount: ''
  });
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    loadBalances();
  }, []);

  const loadBalances = async () => {
    try {
      const response = await walletAPI.getBalances();
      setBalances(response.data.filter(b => b.crypto !== 'AED'));
    } catch (error) {
      toast.error('Failed to load balances');
    }
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    setConverting(true);

    try {
      const response = await convertAPI.convertToAED(convertData);
      toast.success(`Converted ${convertData.amount} ${convertData.from_crypto} to ${formatCurrency(response.data.converted_amount)}`);
      setConvertData({ from_crypto: 'BTC', amount: '' });
      loadBalances();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  const selectedBalance = balances.find(b => b.crypto === convertData.from_crypto);
  const rate = CRYPTO_RATES[convertData.from_crypto] || 0;
  const estimatedAED = convertData.amount ? parseFloat(convertData.amount) * rate : 0;

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-primary font-bold text-white mb-2">CONVERT</h1>
          <p className="text-gray-400 font-secondary mb-8">Convert crypto to AED</p>

          <div className="glass-card p-8 rounded-2xl">
            <form onSubmit={handleConvert} className="space-y-6">
              {/* From Crypto */}
              <div>
                <Label className="text-gray-300 mb-2 block">From Cryptocurrency</Label>
                <Select value={convertData.from_crypto} onValueChange={(value) => setConvertData({ ...convertData, from_crypto: value })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="convert-from-crypto-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A051E] border-white/10">
                    {Object.keys(CRYPTO_RATES).map(crypto => (
                      <SelectItem key={crypto} value={crypto} className="text-white hover:bg-white/10">
                        {CRYPTO_SYMBOLS[crypto]} {CRYPTO_NAMES[crypto]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedBalance && (
                  <p className="text-gray-400 text-sm mt-2 font-mono">Available: {selectedBalance.balance} {convertData.from_crypto}</p>
                )}
              </div>

              {/* Amount */}
              <div>
                <Label className="text-gray-300 mb-2 block">Amount to Convert</Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="any"
                    placeholder="0.00"
                    value={convertData.amount}
                    onChange={(e) => setConvertData({ ...convertData, amount: e.target.value })}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 pr-16 font-mono text-2xl py-6"
                    required
                    data-testid="convert-amount-input"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-mono text-lg">
                    {convertData.from_crypto}
                  </div>
                </div>
              </div>

              {/* Conversion Arrow */}
              <div className="flex items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-[#7000FF]/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#7000FF]" />
                </div>
              </div>

              {/* To AED */}
              <div>
                <Label className="text-gray-300 mb-2 block">You'll Receive (AED)</Label>
                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <div className="text-4xl font-mono font-bold text-[#00F0FF]" data-testid="estimated-aed-amount">
                    {estimatedAED > 0 ? formatCurrency(estimatedAED) : 'د.إ 0.00'}
                  </div>
                  <div className="text-gray-400 text-sm mt-2 font-mono">
                    Rate: 1 {convertData.from_crypto} = {formatCurrency(rate)}
                  </div>
                </div>
              </div>

              {/* Summary */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-400">Conversion Fee</span>
                  <span className="text-white font-mono">0%</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Processing Time</span>
                  <span className="text-white">Instant</span>
                </div>
              </div>

              <Button
                type="submit"
                disabled={converting}
                className="w-full bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full py-6 text-lg font-bold neon-glow-primary"
                data-testid="confirm-convert-btn"
              >
                {converting ? 'Processing...' : (
                  <>
                    Convert to AED <ArrowRight className="ml-2 inline" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

export default Convert;
